<template>

  <Form @submit="onFormSubmit" :validation-schema="schema"
    class="space-y-4 text-gray-700 mx-5 -my-16 md:-my-36 lg:-my-24">

    <div class="h-screen flex items-center justify-center">

      <div
        class="container mx-auto w-full md:w-8/12 lg:w-6/12 p-5 bg-white bg-opacity-90 rounded-lg shadow-2xl space-y-2 text-center">

        <img src="@/assets/logo_chico.png" class="mx-auto" />

        <div class="py-5">
          <div class="font-bold">{{ $t('titulo.login') }}</div>
          <div class="text-sm">{{ $t('subtitulo.login') }}</div>
        </div>


        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
          <div class="w-full px-2">
            <label class="block mb-1">{{ $t('label.documento') }}</label>
            <Field name="dni" v-slot="{ field, errors }" v-model="formData.dni">
              <InputText v-bind="field" type="text" class="input-base" :placeholder="$t('label.documento')" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error"><ErrorMessage name="dni" /></span>
            </Field>
          </div>
        </div>

        <div class="flex flex-wrap items-center pt-3" v-show="pedirClaveActualizacion">
          <div class="font-bold text-red-500 w-full text-center">{{ $t('label.dni.existe') }}</div>
        </div>

        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0" v-show="pedirClaveActualizacion">
          <div class="w-full px-2">
            <label class="block mb-1 text-center">{{ $t('label.clave.actualizacion') }}</label>
            <Field name="claveActualizacion" v-slot="{ field, errors }" v-model="formData.claveActualizacion">
              <InputText v-bind="field" type="password" class="input-base" :placeholder="$t('label.clave.actualizacion')" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="claveActualizacion" />
              </span>
            </Field>
          </div>
        </div>

        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0" v-show="pedirClaveActualizacion">
          <div class="w-full px-2">
            <button @click="mostrarDialogRecuperarPassword" type="button">{{ $t('label.olvide.mi.clave') }}</button>
          </div>
        </div>

        <div class="flex flex-wrap space-y-4 md:space-y-0 pt-1 pb-5  items-center justify-center"
          v-show="!pedirClaveActualizacion">
          <button type="submit"
            class="w-full lg:w-auto px-4 py-2 cursor-pointer border-[#243665] hover:bg-[#243665] text-[#243665] font-semibold hover:text-white border rounded-xl"
            :disabled="isSubmitting">
            <i v-show="isSubmitting" class="pi pi-spin pi-spinner mr-2"></i>
            <i v-show="!isSubmitting" class="pi pi-check mr-2"></i> {{ $t('boton.validar.documento') }}
          </button>
        </div>

        <div
          class="flex flex-col lg:flex-row space-x-0 lg:space-x-2 space-y-2 lg:space-y-0 pt-5 items-center justify-center"
          v-show="pedirClaveActualizacion">

          <button v-if="acreditado && acreditado.estadoAcreditado?.id == 2" type="button" @click="verCredencial"
            class="w-full lg:w-auto px-4 py-2 bg-transparent border-[#243665] hover:bg-[#243665] text-[#243665] font-semibold hover:text-white border rounded-xl"
            :disabled="isSubmitting">
            <i class="pi pi-arrow-circle-down mr-2"></i> {{ $t('global.ver.credencial') }}
          </button>

          <button type="submit"
            class="w-full lg:w-auto px-4 py-2 bg-transparent border-[#243665] hover:bg-[#243665] text-[#243665] font-semibold hover:text-white border rounded-xl"
            :disabled="isSubmitting">
            <i v-show="isSubmitting" class="pi pi-spin pi-spinner mr-2"></i>
            <i v-show="!isSubmitting" class="pi pi-check mr-2"></i> {{ $t('boton.modificar.datos') }}
          </button>

          <button @click="cancelarAuth" type="button"
            class="w-full lg:w-auto px-4 py-2 bg-transparent border-red-600 hover:bg-red-600 text-red-600 font-semibold hover:text-white border rounded-xl"
            :disabled="isSubmitting">
            <i class="pi pi-times mr-2"></i>
            {{ $t('boton.cancelar') }}
          </button>

        </div>

        <div class="flex items-center gap-2 pt-3 my-4 justify-center">
          <button @click="changeLanguage('es')" class="hover:opacity-70 transition">
            <img src="@/assets/ar.svg" class="h-8" />
          </button>
          <button @click="changeLanguage('en')" class="hover:opacity-70 transition">
            <img src="@/assets/us.svg" class="h-8" />
          </button>
        </div>

      </div>

    </div>

  </Form>

  <BlockUI :blocked="isSubmitting" />
  <RecuperarPasswordView v-if="recuperarPasswordVisible" :closeFunction="cerrarDialogoRecuperarPassword"
    :acreditado="acreditado" />

</template>

<script lang="ts">
import * as yup from 'yup';
import { defineComponent } from 'vue';
import { Field, Form, ErrorMessage } from 'vee-validate';
import Swal from 'sweetalert2'

import InputText from 'primevue/inputtext';
import BlockUI from 'primevue/blockui';

import suiteService, { Acreditado } from 'qreventos-suite/services';
import suiteConfig from '@/services/suite.config';
import idiomaServices from '@/services/idioma.services';
import RecuperarPasswordView from '@/components/modal/RecuperarPasswordView.vue';

export default defineComponent({
  name: 'FormSection',
  components: {
    InputText,
    Field, ErrorMessage, Form, BlockUI,
    RecuperarPasswordView
  },
  data() {
    return {
      isSubmitting: false,
      acreditado: undefined as unknown as Acreditado,
      pedirClaveActualizacion: false,
      recuperarPasswordVisible: false,
      formData: {
        dni: '',
        claveActualizacion: ''
      }
    };
  },
  computed: {
    schema() {
      return yup.object({
        dni: yup.string().required(this.$t('errors.dni.required')).min(6, this.$t('errors.dni.min')).matches(/^[a-zA-Z0-9]+$/, this.$t('errors.dni.invalid')),

        claveActualizacion: yup.string().test('clave-condicional', this.$t('errors.clave.required'),
          (value) => {
            if (!this.pedirClaveActualizacion) {
              return true;
            }
            return !!value && value.length >= 3;
          }
        )
      });
    },
  },
  methods: {
    changeLanguage(lang: string) {
      this.$i18n.locale = lang;
      idiomaServices.updateIdioma(lang);
    },
    async cancelarAuth() {
      this.acreditado = undefined as unknown as Acreditado;
      this.pedirClaveActualizacion = false;
      this.formData.dni = "";
    },
    async validarDni() {
      this.isSubmitting = true;

      try {
        this.acreditado = await suiteService.findAcreditadoByFilters(suiteConfig.evento, "dni=" + this.formData.dni) as Acreditado;
        if (!this.acreditado) {
          this.$router.push({ path: '/formulario/dni/' + this.formData.dni });
          return;
        }

        if (this.acreditado) {
          this.pedirClaveActualizacion = true;
          return;
        }

      } catch (error) {
        this.isSubmitting = false;
        Swal.fire({ title: "Error", text: error + '', icon: "error" });
      } finally {
        this.isSubmitting = false;
      }
    },
    /**
     *  Gestión de dialogo para recuperar password 
     */
    mostrarDialogRecuperarPassword() {
      this.recuperarPasswordVisible = true;
    },
    cerrarDialogoRecuperarPassword(aceptado: boolean = false) {
      this.recuperarPasswordVisible = false;

      if (aceptado) {
        Swal.fire({ title: this.$t('global.atencion'), html: this.$t('mensaje.clave.enviada'), icon: "success" });
      }
    },
    /**
     *  FIN Gestión de dialogo para recuperar password 
     */
    async verCredencial() {
      this.isSubmitting = true;

      try {
        const hash: string = await suiteService.auth(suiteConfig.evento, this.formData.dni, this.formData.claveActualizacion, 1) as string; // 1 - DNI
        if (!hash) {
          this.isSubmitting = false;
          Swal.fire({ title: this.$t('global.atencion'), html: this.$t('errores.clave.incorrecta'), icon: "error" });
          return;
        }

        this.$router.push({ path: '/credencial/' + hash }).then(() => { this.$router.go(0) });

      } catch (error) {
        this.isSubmitting = false;
        Swal.fire({ title: "Error", html: this.$t('errores.clave.incorrecta') + '<br/><br/>[' + error + ']', icon: "error" });
      } finally {
        this.isSubmitting = false;
      }
    },
    onFormSubmit() {
      if (!this.pedirClaveActualizacion) {
        this.validarDni();
      } else {
        this.onSubmit();
      }
    },
    async onSubmit() {
      this.isSubmitting = true;

      try {
        const hash: string = await suiteService.auth(suiteConfig.evento, this.formData.dni, this.formData.claveActualizacion, 1) as string; // 1 - DNI
        if (!hash) {
          this.isSubmitting = false;
          Swal.fire({ title: this.$t('global.atencion'), html: this.$t('errores.clave.incorrecta'), icon: "error" });
          return;
        }

        this.$router.push({ path: '/formulario/hash/' + hash });

      } catch (error) {
        this.isSubmitting = false;
        Swal.fire({ title: "Error", html: this.$t('errores.clave.incorrecta') + '<br/><br/>[' + error + ']', icon: "error" });
      } finally {
        this.isSubmitting = false;
      }
    },
  }
});
</script>